import { useState, useCallback } from 'react';
import supabase from '@/supabase';
import { useEntitlements } from './use-entitlements';
import type {
  ContentType,
  UnlockSuccessResponse,
  UnlockErrorResponse,
  UnlockResponse,
} from '@/types/content-unlock';
import { CONTENT_PRICING } from '@/types/content-unlock';

interface UseContentUnlockReturn {
  unlockContent: (
    contentId: string,
    contentType: ContentType,
    creditsRequired?: number
  ) => Promise<UnlockSuccessResponse | null>;
  checkIfUnlocked: (contentId: string, contentType: ContentType) => Promise<boolean>;
  checkIfUnlockedBatch: (contentIds: string[], contentType: ContentType) => Promise<string[]>;
  isUnlocking: boolean;
  error: string | null;
  clearError: () => void;
}

export function useContentUnlock(): UseContentUnlockReturn {
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { refresh: refreshEntitlements, credits } = useEntitlements();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const unlockContent = useCallback(
    async (
      contentId: string,
      contentType: ContentType,
      creditsRequired?: number
    ): Promise<UnlockSuccessResponse | null> => {
      setIsUnlocking(true);
      setError(null);

      // Use provided credits or default pricing
      const finalCreditsRequired = creditsRequired ?? CONTENT_PRICING[contentType];

      // Pre-check credits on client side to give immediate feedback
      if (credits < finalCreditsRequired) {
        setError(`Not enough credits. You have ${credits} credits but need ${finalCreditsRequired}.`);
        setIsUnlocking(false);
        return null;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          throw new Error('You must be logged in to unlock content');
        }

        const response = await supabase.functions.invoke<UnlockResponse>(
          'unlock-content',
          {
            body: {
              contentId,
              contentType,
              creditsRequired: finalCreditsRequired,
            },
          }
        );

        if (response.error) {
          throw new Error(response.error.message || 'Failed to unlock content');
        }

        const data = response.data;

        if (!data) {
          throw new Error('No response from unlock service');
        }

        // Check if it's an error response
        if ('error' in data && !('success' in data)) {
          const errorData = data as UnlockErrorResponse;

          // Provide user-friendly error messages
          let userMessage = errorData.message;
          if (errorData.error === 'insufficient_credits') {
            userMessage = `Not enough credits. You have ${errorData.creditsRemaining ?? 0} but need ${errorData.creditsRequired ?? finalCreditsRequired}.`;
          } else if (errorData.error === 'no_entitlements') {
            userMessage = 'Your account is not set up for purchases. Please contact support.';
          }

          throw new Error(userMessage);
        }

        // Success - refresh entitlements to update credit balance
        const successData = data as UnlockSuccessResponse;

        // Only refresh if credits were actually spent
        if (!successData.alreadyUnlocked && successData.creditsSpent > 0) {
          await refreshEntitlements();
        }

        return successData;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred';
        console.error('Content unlock error:', message);
        setError(message);
        return null;
      } finally {
        setIsUnlocking(false);
      }
    },
    [credits, refreshEntitlements]
  );

  const checkIfUnlocked = useCallback(
    async (contentId: string, contentType: ContentType): Promise<boolean> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          return false;
        }

        const { data, error: rpcError } = await supabase.rpc('check_content_unlocked', {
          p_user_id: user.id,
          p_content_id: contentId,
          p_content_type: contentType,
        });

        if (rpcError) {
          console.error('Check unlock status error:', rpcError);
          return false;
        }

        return data === true;
      } catch (err) {
        console.error('Check unlock error:', err);
        return false;
      }
    },
    []
  );

  const checkIfUnlockedBatch = useCallback(
    async (contentIds: string[], contentType: ContentType): Promise<string[]> => {
      if (contentIds.length === 0) {
        return [];
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          return [];
        }

        const { data, error: rpcError } = await supabase.rpc('check_content_unlocked_batch', {
          p_user_id: user.id,
          p_content_ids: contentIds,
          p_content_type: contentType,
        });

        if (rpcError) {
          console.error('Batch check unlock status error:', rpcError);
          return [];
        }

        return (data as string[]) ?? [];
      } catch (err) {
        console.error('Batch check unlock error:', err);
        return [];
      }
    },
    []
  );

  return {
    unlockContent,
    checkIfUnlocked,
    checkIfUnlockedBatch,
    isUnlocking,
    error,
    clearError,
  };
}
